import { useEffect } from 'react';
import { useLazyQuery } from 'react-apollo';
import { useAuthService } from '@/contexts/authService';
import { UserRaceEntriesQuery } from '@/state/services/gql/user';
import { UserResultSubmissionsQuery } from '@/state/services/gql/resultSubmissions';
import { getEventTimeState } from '@/views/MyEventPage/utils/eventTimeState';
import { add15Hours } from '@/utils/dates';

const isSubmissionsOpen = (entry) => {
  const { isLive } = getEventTimeState(entry);
  return isLive;
};
const useSubmitResultsNotification = ({
  submitResultNotificationTimestamp,
}) => {
  const isSsr = __SERVER__;
  const authService = useAuthService();
  const loggedInUserId = authService.getUserCookie();
  const skipQueries =
    !authService.loggedIn() || !authService.getUserCookie() || isSsr;

  const thresholdDate = add15Hours(
    submitResultNotificationTimestamp
      ? new Date(submitResultNotificationTimestamp)
      : new Date(),
  );

  const [
    fetchRaceEntries,
    { data: raceEntriesData, loading: raceEntriesLoading },
  ] = useLazyQuery(UserRaceEntriesQuery, {
    variables: {
      id: loggedInUserId,
      thresholdDate,
    },
    errorPolicy: 'all',
    skip: skipQueries,
  });

  const [
    fetchUserResults,
    { data: userResultData, loading: userResultLoading },
  ] = useLazyQuery(UserResultSubmissionsQuery, {
    variables: {
      primaryIdentityId: loggedInUserId,
    },
    errorPolicy: 'all',
    skip: skipQueries,
  });

  useEffect(() => {
    if (__SERVER__) {
      return;
    }

    fetchRaceEntries();
    fetchUserResults();
  }, []);

  const upcomingEvents = raceEntriesData?.profile?.upcomingRaceEntries;
  const completedEvents = raceEntriesData?.profile?.completedRaceEntries;
  const allEvents = upcomingEvents?.concat(completedEvents);

  const userResultSumissions =
    userResultData?.userByPrimaryIdentityId?.resultSubmissions;

  const resultSubmissionEventIdSet = new Set(); // eventIds that the user has already submitted results
  const submitResultEvents = []; // events that the user can submit results

  if (userResultSumissions && upcomingEvents) {
    userResultSumissions.forEach(({ eventId }) =>
      resultSubmissionEventIdSet.add(parseInt(eventId, 10)),
    );

    // Sometimes completed events will still have an open submission window
    // therefore we check all events.
    allEvents.forEach((entry) => {
      const { bookingId, eventSummary } = entry;
      if (
        isSubmissionsOpen(entry) &&
        !resultSubmissionEventIdSet.has(parseInt(eventSummary.eventId, 10))
      ) {
        submitResultEvents.push({ bookingId, eventSummary });
      }
    });
  }

  return {
    loading: raceEntriesLoading || userResultLoading,
    submitResultEvents,
  };
};

export default useSubmitResultsNotification;
